import { create } from "zustand";
import type { SourceMeta } from "@ando/ui";
import type { WorkspaceTab } from "@/features/workspace-router/state/workspaceStore";

/**
 * Sources store — citation-drawer mode only (May 2026 SOTA simplification).
 *
 * Historical context (deprecated branches removed):
 *
 *   v2 originally exposed 4 modes — ``source`` (citation drawer),
 *   ``inbox`` (email/chat row drawer), ``artifact`` (split doc viewer
 *   drawer), ``calendar`` (event detail drawer). The May 2026 user
 *   feedback was explicit: drawers from the right are JARRING — auto-open
 *   must land the content INLINE in the work pane, with the
 *   ``<SourceRail>`` chips providing alternative-source navigation. The
 *   inline pattern lives in ``documents.tsx`` / ``conversations.tsx`` /
 *   ``calendar.tsx`` (Opzione B); the drawer-side branches were dropped.
 *
 *   What remains: the ``source`` mode — citation pin-point drawer used
 *   for FUTURE deep citation anchors (page + bbox + highlighted text)
 *   when the user explicitly clicks an inline ``[n]`` chip. Today the BE
 *   does not emit ``[n]`` markers in the markdown, so this surface is
 *   effectively unused — but the store API stays in place so we can
 *   re-enable the chip layer without a store rewrite later.
 *
 * Pairing with the workspace store:
 *   - opening a drawer dispatches a ``routeFromAndo(tab)`` on the workspace
 *     store so the user sees the "Aperto da Ando" pulse. That sync lives
 *     in ``useCitationClick.ts``, not here, to keep the store unit-testable.
 */

export type SourcesMode = "source";

export interface SourcesState {
  /** Drawer flavour currently visible. Always ``"source"`` in v2 — kept
   *  as a typed field for forward-compat (new drawer modes can be added
   *  without breaking consumers that already discriminate). */
  mode: SourcesMode;
  /** All currently-open drawers, keyed by source id. */
  openDrawers: Map<string, SourceMeta>;
  /** Id of the drawer currently presented in the active tab (or ``null``). */
  activeId: string | null;
  /** Tab where each drawer was routed — used to scope visibility per tab. */
  drawerTabs: Map<string, WorkspaceTab>;

  /** Open a new drawer (or focus an existing one). */
  openDrawer: (meta: SourceMeta, tab: WorkspaceTab) => void;
  /** Close one drawer (drops it from the map). */
  closeDrawer: (id: string) => void;
  /** Bring a drawer to the front without re-fetching. */
  focusDrawer: (id: string | null) => void;
  /** Close every open drawer (e.g. on logout / conversation switch). */
  closeAll: () => void;
}

export const useSourcesStore = create<SourcesState>((set, _get) => ({
  mode: "source",
  openDrawers: new Map(),
  activeId: null,
  drawerTabs: new Map(),

  openDrawer: (meta, tab) =>
    set((s) => {
      const drawers = new Map(s.openDrawers);
      drawers.set(meta.id, meta);
      const tabs = new Map(s.drawerTabs);
      tabs.set(meta.id, tab);
      return {
        mode: "source",
        openDrawers: drawers,
        drawerTabs: tabs,
        activeId: meta.id,
      };
    }),

  closeDrawer: (id) =>
    set((s) => {
      const drawers = new Map(s.openDrawers);
      drawers.delete(id);
      const tabs = new Map(s.drawerTabs);
      tabs.delete(id);
      const nextActive =
        s.activeId === id ? (drawers.size ? [...drawers.keys()][0] : null) : s.activeId;
      return { openDrawers: drawers, drawerTabs: tabs, activeId: nextActive };
    }),

  focusDrawer: (id) => set({ activeId: id }),

  closeAll: () =>
    set({
      mode: "source",
      openDrawers: new Map(),
      drawerTabs: new Map(),
      activeId: null,
    }),
}));

// ── Selectors ────────────────────────────────────────────────────────────

export const selectActiveDrawer = (s: SourcesState): SourceMeta | null =>
  s.activeId ? (s.openDrawers.get(s.activeId) ?? null) : null;

export const selectDrawersForTab =
  (tab: WorkspaceTab) =>
  (s: SourcesState): SourceMeta[] => {
    const ids = [...s.drawerTabs.entries()].filter(([, t]) => t === tab).map(([id]) => id);
    return ids.map((id) => s.openDrawers.get(id)).filter((m): m is SourceMeta => m !== undefined);
  };
